import React, { useState, useEffect } from 'react';
import api from '../../api/axios';
import StatsCard from '../Common/StatsCard';
import DataTable from '../Common/DataTable';
import AccountCreation from '../AccountCreation';
import BarChart from '../Common/BarChart';
import DonutChart from '../Common/DonutChart';
import LineChart from '../Common/LineChart';
import DashboardHero from '../Common/DashboardHero';

export default function AdminDashboard() {
  const [campuses, setCampuses] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [userStats, setUserStats] = useState({ total: 0, by_role: {} });
  const [selectedCampus, setSelectedCampus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dbInfo, setDbInfo] = useState(null);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    api.get('/admin-settings/database-info')
      .then(r => setDbInfo(r.data))
      .catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try { const r = await api.get('/campuses/'); setCampuses(r.data); } catch (e) { console.error(e); }
    try { const r = await api.get('/users/'); setAllUsers(r.data); } catch (e) { console.error(e); }
    try { const r = await api.get('/students/'); setAllStudents(r.data); } catch (e) { console.error(e); }
    try { const r = await api.get('/users/stats'); setUserStats(r.data); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const filteredCampuses = selectedCampus === 'all'
    ? campuses
    : campuses.filter(c => c.id === parseInt(selectedCampus));

  const filteredUsers = selectedCampus === 'all'
    ? allUsers
    : allUsers.filter(u => {
        const c = campuses.find(x => x.id === parseInt(selectedCampus));
        return u.campus_name === c?.name || Number(u.campus_id) === Number(selectedCampus);
      });

  const filteredStudents = selectedCampus === 'all'
    ? allStudents
    : allStudents.filter(s => {
        const c = campuses.find(x => x.id === parseInt(selectedCampus));
        return s.campus_name === c?.name || Number(s.campus_id) === Number(selectedCampus);
      });

  const revenue = filteredCampuses.length * 45000;

  const campusBarData = campuses.map(c => ({
    label: c.name.split(' ')[0],
    value: allStudents.filter(s => s.campus_name === c.name).length,
    color: '#2563eb',
  }));

  const roleDonutData = [
    { label: 'Teachers', value: userStats.by_role?.teacher || 0, color: '#10b981' },
    { label: 'Students', value: allStudents.length, color: '#3b82f6' },
    { label: 'Parents', value: userStats.by_role?.parent || 0, color: '#f59e0b' },
    {
      label: 'Admins',
      value: (userStats.by_role?.admin || 0) + (userStats.by_role?.principal || 0) + (userStats.by_role?.superintendent || 0),
      color: '#8b5cf6',
    },
  ].filter(d => d.value > 0);

  const genderDonutData = [
    { label: 'Male', value: filteredStudents.filter(s => s.gender === 'Male').length, color: '#3b82f6' },
    { label: 'Female', value: filteredStudents.filter(s => s.gender === 'Female').length, color: '#ec4899' },
    { label: 'Other', value: filteredStudents.filter(s => s.gender === 'Other').length, color: '#9ca3af' },
  ].filter(d => d.value > 0);

  const enrollmentTrend = [
    { label: 'Apr', value: Math.round(allStudents.length * 0.6) },
    { label: 'May', value: Math.round(allStudents.length * 0.7) },
    { label: 'Jun', value: Math.round(allStudents.length * 0.75) },
    { label: 'Jul', value: Math.round(allStudents.length * 0.85) },
    { label: 'Aug', value: Math.round(allStudents.length * 0.9) },
    { label: 'Sep', value: allStudents.length },
  ];

  return (
    <div>
      <DashboardHero
        color="indigo"
        icon="fa-user-shield"
        title="Admin Dashboard"
        subtitle={`${campuses.length} campuses · ${allUsers.length} users · ${allStudents.length} students`}
      >
        <button
          onClick={fetchData}
          className="bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-lg text-sm font-medium transition"
        >
          <i className="fas fa-sync-alt mr-1"></i> Refresh
        </button>
      </DashboardHero>

      {/* DB Upgrade notice */}
      {dbInfo?.should_upgrade && (
        <div className="mb-4 p-4 rounded-lg bg-amber-50 border-l-4 border-amber-500 flex items-start gap-3">
          <i className="fas fa-exclamation-triangle text-amber-600 text-xl mt-0.5"></i>
          <div className="flex-1">
            <p className="font-semibold text-amber-900">Database upgrade recommended</p>
            <p className="text-sm text-amber-800 mt-1">
              {dbInfo.reason}{' '}
              <a href="/dashboard/admin-settings" className="underline font-medium">
                Go to Admin Settings →
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Campus filter */}
      <div className="bg-white p-4 rounded shadow mb-4 flex items-center gap-4 flex-wrap">
        <label className="font-semibold text-sm">🏫 Filter by Campus:</label>
        <select
          className="p-2 border rounded"
          value={selectedCampus}
          onChange={(e) => setSelectedCampus(e.target.value)}
        >
          <option value="all">All Campuses</option>
          {campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatsCard label="Campuses" value={filteredCampuses.length} icon="fa-building" />
        <StatsCard label="Users" value={filteredUsers.length} icon="fa-users" />
        <StatsCard label="Students" value={filteredStudents.length} icon="fa-user-graduate" />
        <StatsCard label="Revenue" value={`$${revenue}`} icon="fa-dollar-sign" />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <BarChart title="🏫 Students per Campus" data={campusBarData} />
        <DonutChart title="👥 User Distribution" data={roleDonutData} />
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <LineChart title="📈 Enrollment Trend" data={enrollmentTrend} />
        <DonutChart title="⚖️ Gender Distribution" data={genderDonutData} />
      </div>

      {/* Campuses table */}
      <div className="bg-white p-4 rounded shadow mb-6">
        <h2 className="text-lg font-semibold mb-3">Campuses</h2>
        {loading ? <p className="text-gray-500">Loading...</p> : (
          <DataTable
            columns={['Name', 'Address', 'Principal', 'Status']}
            data={filteredCampuses.map(c => {
              const p = allUsers.find(u => u.id === c.principal_id);
              return {
                Name: c.name,
                Address: c.address || '—',
                Principal: p ? `${p.first_name} ${p.last_name}` : 'Not Assigned',
                Status: c.is_active ? 'Active' : 'Inactive',
              };
            })}
          />
        )}
      </div>

      {/* Account creation panel */}
      <AccountCreation />
    </div>
  );
}